import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  Card, Chip, DataTable, EmptyState, Icon, Modal, Pane, PaneHead, type TableRow,
} from '@/components/kit';
import type { FlowRole } from '../shared/useFlows';
import {
  itemsApi, useLibraryRefresh, type CatalogItem, type FlowItem, type ItemConsumption, type ItemProduces,
} from './useLibraryApi';
import { ROLE_CHIP, ROLE_LABEL, InlineSelect, InlineEdit, useRoleLabel, DeskOnly, PhoneRowList } from './libraryBits';
import { useNarrowViewport } from '@/components/kit/useNarrow';

/**
 * Library ▸ Flows ▸ Items (`g_libitems`, f15) — the vocabulary the chains are
 * built from — and its editor (`g_l15n`, f15-new), which is the same pane with
 * nothing in it: "+ New item" opens `ItemEditorPane` with a null item, so the
 * screen a clinic writes its first word on and the screen it corrects the word
 * on are one component and cannot drift apart.
 *
 * WHY ITEMS AND NOT FREE TEXT. A flow used to carry its steps as words, so
 * "Dilation" in one chain and "dilating drops" in another were two different
 * things to everyone who executed them. An item is written once: who does it,
 * what the executor reads, how long it waits, what it needs first, what it
 * produces, whether consent is taken and whether it bills. Change the item and
 * every flow using it updates — which is exactly what the canvas promises and
 * what makes "+ Add step — from Items" mean something.
 *
 * PACKS SEED THE STARTER SET. There is no pack loader yet, so a clinic's
 * vocabulary starts empty and "+ New item" writes the first word.
 */

const PRODUCES_LABEL: Record<ItemProduces, string> = {
  nothing: 'nothing',
  document: 'a document',
  values: 'a values form',
};

/**
 * The canvas's WHAT THE EXECUTOR READS column, assembled from the item.
 *
 * The wait wears the kit's clock, never "⏱": icons.md's rule is that an emoji
 * carries its own colour and cannot inherit the row's.
 */
export function itemReads(i: FlowItem, catalogName?: string | null): ReactNode {
  const desc = itemCardLine(i);
  const parts: ReactNode[] = desc ? [desc] : [];
  parts.push(...itemRidesParts(i, catalogName));
  return parts.map((x, n) => <span key={n}>{n ? ' · ' : ''}{x}</span>);
}

/* THE LIST TAKES THE FIRST SENTENCE. The card the executor reads carries the
   whole instruction; the row is the line that tells them which task it is,
   and the second sentence is detail they read when they open it. */
function itemCardLine(i: FlowItem): string {
  return (i.description ?? '').trim().split(/(?<=\.)\s+/)[0].replace(/\.$/, '');
}

/** What rides WITH the item into every flow — the wait, the catalog link, consent, billing. */
function itemRidesParts(i: FlowItem, catalogName?: string | null): ReactNode[] {
  const parts: ReactNode[] = [];
  if (i.waitMin) parts.push(<><Icon name="clock" size="sm" /> {i.waitMin} min wait</>);
  if (catalogName) parts.push('catalog ✓');
  if (i.consentRequired) parts.push('consent');
  parts.push(i.billable ? 'billable' : 'not billable');
  return parts;
}

export function itemRows(
  items: FlowItem[],
  onEdit: (id: string) => void,
  catalog: Map<string, CatalogItem>,
  needs: Map<string, string>,
  labels: Record<FlowRole, string> = ROLE_LABEL,
): TableRow[] {
  return items.map((i) => {
    const catalogName = i.catalogItemId ? catalog.get(i.catalogItemId)?.name : null;
    const needsLine = i.needsItemId ? `needs: ${needs.get(i.needsItemId) ?? 'another item'}` : null;
    const rides = [...itemRidesParts(i, catalogName), ...(needsLine ? [needsLine] : [])];
    return {
      id: i.id,
      cells: {
        name: (
          <>
            <span className="t-strong">{i.name}</span>{' '}
            <button type="button" className="dchip dchip-brand"
              onClick={(e) => { e.stopPropagation(); onEdit(i.id); }}>
              ✎ Edit
            </button>
          </>
        ),
        role: <Chip kind={ROLE_CHIP[i.assigneeRole]} small>{labels[i.assigneeRole]}</Chip>,
        reads: (
          <span className="t-sec mut">
            {itemReads(i, catalogName)}
            {needsLine ? ` · ${needsLine}` : ''}
          </span>
        ),
        /* Settings' board (`g_flowitems`) splits the one column in two — the
           WHOLE card text (it has the width the Library's combined column
           spends on the extras), and what rides with the item into every flow. */
        card: <span className="t-sec mut">{(i.description ?? '').trim() || '—'}</span>,
        rides: (
          <span className="t-sec mut">
            {rides.map((x, n) => <span key={n}>{n ? ' · ' : ''}{x}</span>)}
          </span>
        ),
      },
    };
  });
}

export function ItemsMain({ items, loading, failed, selectedId, onSelect, onEdit, query, catalog, note, split, onNew }: {
  items: FlowItem[];
  loading: boolean;
  failed?: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  query: string;
  catalog: CatalogItem[];
  /** What sits under the list — each entrance's board writes its own. */
  note?: ReactNode;
  /** Settings' four columns (Item · Executor · What the card says · Rides with it) instead of the Library's three. */
  split?: boolean;
  /** The phone's "+ New item" — a dashed card at the foot of the list. */
  onNew?: () => void;
}) {
  const labels = useRoleLabel();
  const phone = useNarrowViewport();
  const q = query.trim().toLowerCase();
  const catMap = new Map(catalog.map((c) => [c.id, c]));
  const names = new Map(items.map((i) => [i.id, i.name]));
  const shown = q
    ? items.filter((i) => `${i.name} ${i.description ?? ''} ${i.assigneeRole}`.toLowerCase().includes(q))
    : items;
  if (phone && !split) {
    return (
      <PhoneRowList
        rows={shown.map((i) => {
          const catalogName = i.catalogItemId ? catMap.get(i.catalogItemId)?.name : null;
          const needsLine = i.needsItemId ? `needs: ${names.get(i.needsItemId) ?? 'another item'}` : null;
          return {
            id: i.id,
            selected: i.id === selectedId,
            title: i.name,
            trailing: (
              <>
                <Chip kind={ROLE_CHIP[i.assigneeRole]} small>{labels[i.assigneeRole]}</Chip>
                <button type="button" className="dchip" style={{ border: '1px solid var(--stroke-200)', color: 'var(--ink-600)', background: 'var(--surface-0)' }}
                  onClick={(e) => { e.stopPropagation(); onEdit(i.id); }}>Edit</button>
              </>
            ),
            lines: <>{itemReads(i, catalogName)}{needsLine ? ` · ${needsLine}` : ''}</>,
            onOpen: () => onSelect(i.id),
          };
        })}
        empty={(
          <EmptyState
            title={failed ? 'The items did not load' : loading ? 'Loading…' : q ? `No item matches “${query}”` : 'No items yet'}
            sub={failed ? 'The read did not come back. Reload the page to try again.'
              : 'The vocabulary a step is picked from — a dilation, an OCT, a consent. + New item writes the first one.'}
          />
        )}
        foot={onNew ? (
          <button type="button" onClick={onNew}
            style={{ color: 'var(--brand-600)', fontWeight: 600, display: 'block', width: '100%', marginTop: 4, padding: 14, border: '1px dashed var(--stroke-200)', borderRadius: 'var(--r-md)', background: 'none', textAlign: 'start', font: 'inherit' }}>
            + New item
          </button>
        ) : null}
      />
    );
  }
  return (
    <>
      <DataTable
        columns={split
          ? [
            { key: 'name', label: 'Item', width: 30 },
            { key: 'role', label: 'Executor', width: 14, role: 'trailing' },
            { key: 'card', label: 'What the card says', width: 36 },
            { key: 'rides', label: 'Rides with it', width: 20 },
          ]
          : [
            { key: 'name', label: 'Item', width: 46 },
            /* On a phone card (g_l14) the role is the chip on the right;
               what the executor reads stacks under the name. */
            { key: 'role', label: 'Role', width: 16, role: 'trailing' },
            { key: 'reads', label: 'What the executor reads', width: 38 },
          ]}
        rows={itemRows(shown, onEdit, catMap, names, labels)}
        selectedId={selectedId}
        onSelect={(r) => onSelect(r.id)}
        caption="Items"
        empty={
          <EmptyState
            title={failed ? 'The items did not load'
              : loading ? 'Loading…'
              : q ? `No item matches “${query}”` : 'No items yet'}
            sub={failed
              ? 'The read did not come back. Reload the page to try again.'
              : 'The vocabulary a step is picked from — a dilation, an OCT, a consent. + New item writes the first one.'}
          />
        }
      />
      {note ?? (
        <>
          <DeskOnly>
          <p className="t-cap mut" style={{ padding: '12px 0 0' }}>
            Packs seed the starter set · doctors and the clinic manager create and edit (logged) ·
            executors read, never write. Change an item once and every flow using it updates.
          </p>
          <Card style={{ borderColor: 'var(--stroke-200)' }}>
            <p className="t-sec" style={{ color: 'var(--ink-600)' }}>
              A nebulizer session in an eye clinic’s library is not a mistake — items are pack data, so a
              chest clinic and an eye clinic differ only in what is written here.
            </p>
          </Card>
          </DeskOnly>
        </>
      )}
    </>
  );
}

/**
 * THE EDITOR — its own component with its own state (the shell republishes the
 * pane whenever its deps change, and a form living in that closure would lose
 * every keystroke). Keyed by the item's id.
 */
export function ItemEditorPane({ item, items, catalog, onSaved, onCancel }: {
  item: FlowItem | null;
  /** A new item is a dialog over the list (g_l15n); this closes it. */
  onCancel?: () => void;
  items: FlowItem[];
  catalog: CatalogItem[];
  onSaved: (i: FlowItem) => void;
}) {
  const refresh = useLibraryRefresh();
  const labels = useRoleLabel();
  const [name, setName] = useState(item?.name ?? '');
  const [role, setRole] = useState<FlowRole>(item?.assigneeRole ?? 'nurse');
  const [desc, setDesc] = useState(item?.description ?? '');
  const [wait, setWait] = useState<number | null>(item?.waitMin ?? null);
  const [needs, setNeeds] = useState<string | null>(item?.needsItemId ?? null);
  const [produces, setProduces] = useState<ItemProduces>(item?.produces ?? 'nothing');
  const [consent, setConsent] = useState(item?.consentRequired ?? false);
  const [billable, setBillable] = useState(item?.billable ?? false);
  const [catalogId, setCatalogId] = useState<string | null>(item?.catalogItemId ?? null);
  const [consumes, setConsumes] = useState<ItemConsumption[]>(item?.consumes ?? []);
  const [repCount, setRepCount] = useState<number | null>(item?.repeats?.count ?? null);
  const [repDays, setRepDays] = useState<number | null>(item?.repeats?.everyDays ?? null);
  const [saving, setSaving] = useState(false);

  const valid = name.trim().length >= 2;
  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        assigneeRole: role,
        description: desc.trim() || undefined,
        waitMin: wait || null,
        needsItemId: needs || null,
        produces,
        consentRequired: consent,
        billable,
        catalogItemId: billable ? catalogId : null,
        consumes,
        repeats: repCount && repCount >= 2 ? { count: repCount, everyDays: repDays && repDays >= 1 ? repDays : 7 } : null,
      };
      const saved = item ? await itemsApi.update(item.id, body) : await itemsApi.create(body);
      await refresh();
      toast.success(item ? 'Item saved' : 'Item created');
      onSaved(saved);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'The item did not save.');
    } finally {
      setSaving(false);
    }
  };

  const leftCol = (
    <>
      {/* A NEW ITEM HAS NO NAME YET, so the name is a field you can see —
          an untitled heading is not a place to type. */}
      {!item ? (
        <Card>
          <p className="t-ovl mut">NAME</p>
          <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Item name"
            placeholder="Dilation" className="t-strong mt4 dr-titlefield" style={{ width: '100%' }} />
        </Card>
      ) : null}

      {/* THE VALUE, THEN THE MARK — the board shows the sentence in quotes and
          opens a field when the pencil is asked for. */}
      <Card>
        <p className="t-ovl mut">DESCRIPTION — WHAT THE EXECUTOR READS ON THEIR CARD</p>
        <p className="t-sec sec mt4">
          <InlineEdit value={desc} onCommit={setDesc} label="What the executor reads" multiline rows={2}
            placeholder="Instill mydriatic drops per protocol. Both eyes unless the order says one."
            display={desc.trim() ? `“${desc.trim()}”` : undefined} />
        </p>
      </Card>

      {/* The board's `.minirow` — three cards abreast, no margin of their own. */}
      <div className="dr-minirow" style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Card className="grow" style={{ flex: 1, margin: 0 }}>
          <p className="t-ovl mut">ROLE</p>
          <p className="t-strong mt4">
            <InlineSelect value={role} onChange={(v) => setRole(v as FlowRole)} aria-label="Who does it"
              options={(Object.keys(labels) as FlowRole[]).map((r) => ({ value: r, label: labels[r] }))} />
          </p>
        </Card>
        <Card className="grow" style={{ flex: 1, margin: 0 }}>
          <p className="t-ovl mut">WAIT</p>
          <p className="t-strong mt4">
            <InlineEdit value={wait ? String(wait) : ''} label="Wait, minutes" numeric mark="glyph"
              onCommit={(v) => setWait(v && Number(v) > 0 ? Number(v) : null)}
              display={wait ? `${wait} min` : <span className="mut">no wait</span>}
              fieldStyle={{ width: 72 }} />
          </p>
        </Card>
        <Card className="grow" style={{ flex: 1, margin: 0 }}>
          <p className="t-ovl mut">NEEDS</p>
          <p className="t-strong mt4">
            <InlineSelect value={needs ?? ''} onChange={(v) => setNeeds(v || null)} aria-label="Needs first"
              options={[{ value: '', label: 'none' },
                ...items.filter((i) => i.id !== item?.id).map((i) => ({ value: i.id, label: i.name }))]} />
          </p>
        </Card>
      </div>

      <Card>
        <p className="t-ovl mut">PRODUCES</p>
        <p className="t-strong mt8">
          <InlineSelect value={produces} onChange={(v) => setProduces(v as ItemProduces)}
            aria-label="What it produces"
            options={(Object.keys(PRODUCES_LABEL) as ItemProduces[]).map((p) => ({ value: p, label: PRODUCES_LABEL[p] }))} />
        </p>
        <p className="t-cap mut mt8">
          nothing · a document through the capture pipeline · or a values form whose fields file as
          data, not paper
        </p>
      </Card>

    </>
  );
  const extrasCol = (
    <>
      <Card>
        <p className="t-ovl mut">CONSUMES (OPTIONAL)</p>
        <p className="t-strong mt4">
          <InlineEdit value={consumesText(consumes)} label="What it uses up" mark="glyph"
            onCommit={(v) => setConsumes(parseConsumes(v))}
            placeholder="1 × nebule (salbutamol) · 1 × mask"
            display={consumes.length ? consumesText(consumes) : <span className="mut">nothing</span>} />
        </p>
      </Card>

      <Card>
        <p className="t-ovl mut">REPEATS (OPTIONAL)</p>
        <p className="t-strong mt4">
          course ×<InlineEdit value={repCount ? String(repCount) : ''} label="Sittings in the course" numeric mark="glyph"
            onCommit={(v) => setRepCount(v && Number(v) >= 2 ? Number(v) : null)}
            display={repCount ? String(repCount) : <span className="mut">1</span>} fieldStyle={{ width: 56 }} />
          {repCount ? <> · every <InlineEdit value={repDays ? String(repDays) : '7'} label="Days between sittings" numeric mark="glyph"
            onCommit={(v) => setRepDays(v && Number(v) >= 1 ? Number(v) : null)}
            display={String(repDays ?? 7)} fieldStyle={{ width: 56 }} /> days</> : null}
        </p>
      </Card>

    </>
  );
  const rightCol = (
    <>
      <Card>
        <div className="flex" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div className="grow" style={{ minWidth: 0 }}>
            <p className="t-strong">Consent required</p>
            <p className="t-cap mut">
              {consent ? 'a consent step runs before it' : 'no consent step — the flow may still add one'}
            </p>
          </div>
          <Switch on={consent} onToggle={() => setConsent((c) => !c)} label="Consent required" />
        </div>
      </Card>

      <Card>
        <div className="flex" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div className="grow" style={{ minWidth: 0 }}>
            <p className="t-strong">Billable</p>
            <p className="t-cap mut">
              {billable
                ? 'it bills through the catalog row below'
                : 'a task that exists here alone — it never reaches a bill'}
            </p>
          </div>
          <Switch on={billable} onToggle={() => setBillable((b) => !b)} label="Billable" />
        </div>
        {billable ? (
          <div style={{ marginTop: 10 }}>
            <p className="t-ovl mut">THE CATALOG ROW IT BILLS THROUGH</p>
            <p className="t-strong mt4">
              <InlineSelect value={catalogId ?? ''} onChange={(v) => setCatalogId(v || null)}
                aria-label="Catalog row"
                options={[{ value: '', label: 'pick one…' },
                  ...catalog.map((c) => ({ value: c.id, label: `${c.name} · ${c.kind}` }))]} />
            </p>
          </div>
        ) : null}
      </Card>

      <p className="t-sec mut mt8">
        → links: <span className="t-sec">{billable
          ? `billable — it bills through ${catalog.find((c) => c.id === catalogId)?.name ?? 'the catalog row you pick'}`
          : 'not billable — it belongs to the visit'}</span>
      </p>
    </>
  );

  if (!item) {
    return (
      <Modal open phoneScreen onClose={() => !saving && onCancel?.()} title="New item" width={900}
        sub="Defined once — then every flow, the Flow button and spoken orders can use it"
        foot={
          <>
            <button type="button" className="btn btn-outline" disabled={saving} onClick={onCancel}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={!valid || saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save item'}
            </button>
          </>
        }>
        <div className="dr-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>{leftCol}</div>
          <div style={{ flex: 1, minWidth: 0 }}>{extrasCol}{rightCol}</div>
        </div>
      </Modal>
    );
  }

  return (
    <Pane
      head={
        <PaneHead
          overline={item ? 'ITEMS ▸ EDITOR' : 'ITEMS ▸ NEW ITEM'}
          title={!item ? 'New item' :
            <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Item name"
              placeholder="Dilation" className="t-title dr-titlefield" />
          }
          sub={<>{labels[role]}{wait ? <> · <Icon name="clock" size="sm" /> {wait} min wait</> : null}</>}
        />
      }
      foot={
        <button type="button" className="btn btn-primary" style={{ flex: 1, minHeight: 44 }}
          disabled={!valid || saving} onClick={() => void save()}>
          {saving ? 'Saving…' : item ? 'Save item' : 'Create item'}
        </button>
      }
    >
      {leftCol}
      {rightCol}
    </Pane>
  );
}

function consumesText(list: ItemConsumption[]) {
  return list.map((c) => `${c.qty} × ${c.name}`).join(' · ');
}

/** "1 × nebule (salbutamol) · mask" → rows; a missing quantity is 1. */
function parseConsumes(text: string): ItemConsumption[] {
  return text.split('·').map((part) => part.trim()).filter(Boolean).map((part) => {
    const m = part.match(/^(\d+)\s*[x×]\s*(.+)$/i);
    return m ? { qty: Number(m[1]), name: m[2].trim() } : { qty: 1, name: part };
  });
}

/** The kit has no switch; this is the board's, scoped to the Library. */
function Switch({ on, onToggle, label }: { on: boolean; onToggle: () => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label}
      className={`dr-switch${on ? ' on' : ''}`} onClick={onToggle}>
      <span className="dr-switch-knob" />
    </button>
  );
}

export { Switch };
